import { Component, Input, OnChanges, SimpleChanges } from '@angular/core'
import { CrmAssignee, SubscriptionRenewalStatus } from '@parkupp/core'
import { ExpiringLeaseRow } from '../expiring-leases/expiring-leases.component'

interface StatItem {
    key: string
    displayName: string
    count: number
    bays: number
    badgeColor?: string
}

interface PieChart {
    series: number[]
    labels: string[]
    colors: string[]
}

const CHART_PALETTE = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#a78bfa', '#14b8a6', '#f97316', '#6366f1', '#ec4899', '#84cc16', '#06b6d4', '#d946ef', '#0ea5e9', '#eab308', '#64748b']

const NON_RENEWAL_COLORS: { [key: string]: string } = {
    'Found Alternative Parking': '#ef4444',
    'Too Expensive': '#f59e0b',
    'No Longer Needs Parking': '#6366f1',
    'Relocated': '#14b8a6',
    'Poor Experience': '#ec4899',
    'Other': '#94a3b8',
    'Not Set': '#6b7280',
}

const RENEWAL_STATUS_COLORS: { [key: string]: string } = {
    'Renewed (Same Parking)': '#4bd181',
    'Renewed (Other Parking)': '#3e8bff',
    'Not Renewed': '#ef4444',
    'Pending': '#f59e0b',
}

function stableColorForString(str: string, palette: string[]): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
        hash = hash & hash
    }
    return palette[Math.abs(hash) % palette.length]
}

@Component({
    selector: 'app-expiring-leases-stats',
    templateUrl: './expiring-leases-stats.component.html',
    styleUrls: ['./expiring-leases-stats.component.scss'],
})
export class ExpiringLeasesStatsComponent implements OnChanges {
    @Input() expiringRows: ExpiringLeaseRow[] = []
    @Input() expiredRows: ExpiringLeaseRow[] = []
    @Input() crmAssigneeMap: { [key: string]: CrmAssignee } = {}

    totalExpiring = 0
    totalExpiringBays = 0
    totalExpiredNotRenewed = 0
    renewalRate = 0
    revenueAtRisk = 0

    byNonRenewalReason: StatItem[] = []
    byParking: StatItem[] = []
    byRenewalStatus: StatItem[] = []
    byLeadStatus: StatItem[] = []
    byUserResponsible: StatItem[] = []
    byLocation: StatItem[] = []

    nonRenewalReasonChart: PieChart = { series: [], labels: [], colors: [] }
    renewalStatusChart: PieChart = { series: [], labels: [], colors: [] }
    parkingChart: PieChart = { series: [], labels: [], colors: [] }
    locationChart: PieChart = { series: [], labels: [], colors: [] }

    readonly pieConfig = {
        type: 'donut' as const,
        height: 300,
        toolbar: { show: false },
    }

    readonly pieLegend = {
        position: 'bottom' as const,
        fontSize: '12px',
        labels: { colors: ['#6b7280'] },
    }

    readonly pieDataLabels = {
        enabled: true,
        formatter: (val: number) => Math.round(val) + '%',
        style: { fontSize: '12px', fontWeight: 600 },
    }

    readonly pieResponsive = [
        {
            breakpoint: 600,
            options: {
                chart: { height: 260 },
                legend: { position: 'bottom' },
            },
        },
    ]

    ngOnChanges(changes: SimpleChanges): void {
        this.computeStats()
    }

    computeStats(): void {
        this.totalExpiring = this.expiringRows.length
        this.totalExpiringBays = this.expiringRows.reduce((sum, r) => sum + (r.summary.baysBooked || 0), 0)

        const notRenewed = this.expiredRows.filter(
            (r) => r.summary.renewalStatus === SubscriptionRenewalStatus.notRenewed || !r.summary.renewalStatus
        )
        this.totalExpiredNotRenewed = notRenewed.length

        const renewed = this.expiredRows.filter(
            (r) =>
                r.summary.renewalStatus === SubscriptionRenewalStatus.renewedSameParking ||
                r.summary.renewalStatus === SubscriptionRenewalStatus.renewedOtherParking
        )
        this.renewalRate = this.expiredRows.length > 0 ? (renewed.length / this.expiredRows.length) * 100 : 0

        this.revenueAtRisk = this.expiringRows.reduce((sum, r) => {
            return sum + (r.summary.feeInCents || 0) * (r.summary.baysBooked || 1)
        }, 0)

        this.byNonRenewalReason = this.groupBy(this.expiredRows, (r) => r.summary.nonRenewalReason || 'Not Set')
        this.byParking = this.groupBy([...this.expiringRows, ...this.expiredRows], (r) => r.summary.denormParkingTitle || 'Unknown')
        this.byRenewalStatus = this.groupBy(this.expiredRows, (r) => this.getRenewalLabel(r.summary.renewalStatus))
        this.byLeadStatus = this.groupBy([...this.expiringRows, ...this.expiredRows], (r) => r.adminClient.leadStatus || 'Not Set')
        this.byUserResponsible = this.groupByUser([...this.expiringRows, ...this.expiredRows])
        this.byLocation = this.groupBy([...this.expiringRows, ...this.expiredRows], (r) => (r.adminClient.broadLocation as string) || 'Not Set')

        this.buildCharts()
    }

    formatCurrency(cents: number): string {
        return 'R' + (cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 0 })
    }

    private buildCharts(): void {
        this.nonRenewalReasonChart = this.buildPieFromStats(this.byNonRenewalReason, NON_RENEWAL_COLORS)
        this.renewalStatusChart = this.buildPieFromStats(this.byRenewalStatus, RENEWAL_STATUS_COLORS)
        this.parkingChart = this.buildPieFromStats(this.byParking, {})
        this.locationChart = this.buildPieFromStats(this.byLocation, {})
    }

    private buildPieFromStats(items: StatItem[], colorMap: { [key: string]: string }): PieChart {
        if (items.length === 0) return { series: [], labels: [], colors: [] }

        return {
            series: items.map((i) => i.count),
            labels: items.map((i) => i.displayName),
            colors: items.map((i) => colorMap[i.key] || stableColorForString(i.key, CHART_PALETTE)),
        }
    }

    private getRenewalLabel(status: string | null): string {
        switch (status) {
            case SubscriptionRenewalStatus.renewedSameParking:
                return 'Renewed (Same Parking)'
            case SubscriptionRenewalStatus.renewedOtherParking:
                return 'Renewed (Other Parking)'
            case SubscriptionRenewalStatus.notRenewed:
                return 'Not Renewed'
            default:
                return 'Pending'
        }
    }

    private groupBy(rows: ExpiringLeaseRow[], keyFn: (r: ExpiringLeaseRow) => string): StatItem[] {
        const map = new Map<string, { count: number; bays: number }>()

        rows.forEach((r) => {
            const key = keyFn(r)
            const existing = map.get(key) || { count: 0, bays: 0 }
            existing.count++
            existing.bays += r.summary.baysBooked || 0
            map.set(key, existing)
        })

        return Array.from(map.entries())
            .map(([key, val]) => ({ key, displayName: key, count: val.count, bays: val.bays }))
            .sort((a, b) => b.count - a.count)
    }

    private groupByUser(rows: ExpiringLeaseRow[]): StatItem[] {
        const map = new Map<string, { count: number; bays: number }>()

        rows.forEach((r) => {
            const userId = r.adminClient.userResponsible || '0'
            const existing = map.get(userId) || { count: 0, bays: 0 }
            existing.count++
            existing.bays += r.summary.baysBooked || 0
            map.set(userId, existing)
        })

        return Array.from(map.entries())
            .map(([key, val]) => {
                const assignee = this.crmAssigneeMap[key]
                return {
                    key,
                    displayName: assignee?.displayName || 'Not Assigned',
                    count: val.count,
                    bays: val.bays,
                    badgeColor: assignee?.badgeColor,
                }
            })
            .sort((a, b) => b.count - a.count)
    }
}
